import type { DivisionsPageState } from './divisions-page.interface';
import type { AppState } from '../../../store';
import type { ConnectedProps } from 'react-redux';
import type { AgencyDto } from 'shared/dtos/agencies.dto';

import { bindActionCreators } from '@reduxjs/toolkit';
import AgencyDivisionsListDraggable from 'components/lists/agency-divisions-draggable-list/agency-divisions-draggable-list';
import { EntityPanel } from 'components/panels/entity-panel/entity-panel';
import { NotFoundMessage } from 'components/template/not-found-message/not-found-message';
import { Spinner } from 'components/template/spinner/spinner';
import { PermissionsContext } from 'contexts';
import React from 'react';
import { connect } from 'react-redux';
import ReportService from 'services/reports.service';
import {
  getAgenciesWithDivisionsThunk,
  getAgencyThunk,
  reorderAgencyDivisionsThunk,
  updateAgencyThunk,
} from 'store/agencies/agencies.thunks';
import {
  getDivisionsThunk,
  getDivisionThunk,
} from 'store/divisions/divisions.thunks';
import { openModal } from 'store/modals/modals.slice';
import { clearSelectedPeople } from 'store/people/people.slice';

import events from '../../../shared/constants/events';
import { MODAL_AGENCY, MODAL_DIVISION } from '../../../shared/constants/modals';
import { vocabulary } from '../../../vocabulary/german';
import { CreateButton } from '../../buttons/create-button/create-button';
import { Container } from '../../template/container/container';
import { PageHeader } from '../../template/page-header/page-header';

const mapStateToProps = (state: AppState) => ({
  divisions: state.divisions,
  agencies: state.agencies,
  isGetAgenciesThunkPending: state.agencies.isGetAgenciesThunkPending,
  currentYearFrozen: state.years.currentYearFrozen,
});
const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getDivisionsThunk,
      getDivisionThunk,
      clearSelectedPeople,
      openModal,
      getAgenciesWithDivisionsThunk,
      getAgencyThunk,
      updateAgencyThunk,

      reorderAgencyDivisionsThunk,
    },
    dispatch,
  );

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

class DivisionsPage extends React.Component<
  PropsFromRedux,
  DivisionsPageState
> {
  static contextType = PermissionsContext;
  context!: React.ContextType<typeof PermissionsContext>;

  state = { locked: false };
  unlock = (): void => {
    this.setState({ locked: false });
  };

  componentDidMount(): void {
    this.props.getAgenciesWithDivisionsThunk();
    document.body.addEventListener(events.UNLOCK.name, this.unlock);
  }

  componentWillUnmount(): void {
    document.body.removeEventListener(events.UNLOCK.name, this.unlock);
  }

  handleSelect = (division): void => {
    if (division.id) {
      this.props.getDivisionThunk(division.id);
      this.props.openModal({ name: MODAL_DIVISION });
    }

    if (division.AgencyId) {
      this.props.getAgencyThunk(division.AgencyId);
    }
  };

  handleCreate = (): void => {
    this.props.openModal({ name: MODAL_DIVISION });
    this.props.clearSelectedPeople();
  };

  handleReorderCurrentAgencyDivisions = ({
    agencyId,
    divisionId,
    orderInAgencyDivisionsList,
  }): void => {
    this.props.reorderAgencyDivisionsThunk({
      agencyId,
      divisionId,
      orderInAgencyDivisionsList,
    });
  };

  handleSelectAgency = (item: AgencyDto) => {
    if (item && item.id) {
      this.props.getAgencyThunk(item.id);
      this.props.openModal({ name: MODAL_AGENCY });
    }
  };

  downloadDivisionsReport = (type) => {
    this.setState({ locked: true });
    const reportsService = new ReportService();
    reportsService.downloadDivisionsReport(type);
  };

  renderCreateDivisionsBtnAgency = (item): JSX.Element | undefined => {
    if (this.context.permissions.createNew === true) {
      return (
        <CreateButton
          text={vocabulary.buttons.createNewDivision}
          onClick={() => this.handleSelectAgency(item)}
        />
      );
    }
  };

  renderEntityPanels() {
    const { agencies, isGetAgenciesThunkPending } = this.props;
    if (isGetAgenciesThunkPending) {
      return <Spinner isFullSize />;
    }

    const NO_DATA = `Nicht gefunden: Ambitionen konnten nicht gefunden werden.`;

    if (agencies.list.length === 0) {
      return <NotFoundMessage message={NO_DATA} />;
    }

    return (
      <div className={'entities-list entities-list_priorities'}>
        {agencies.list.map((item: AgencyDto, index: number) => {
          return (
            <div className={'row agencies-row'} key={index}>
              <div className={'col'}>
                <EntityPanel isFixed title={item.title} id={item.id} />
              </div>
              <div className={'col'}>
                <AgencyDivisionsListDraggable
                  agency={item}
                  handleSelect={this.handleSelect}
                  handleSelectAgency={this.handleSelectAgency}
                  handleReorderCurrentAgencyDivisions={
                    this.handleReorderCurrentAgencyDivisions
                  }
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  render() {
    return (
      <React.Fragment>
        <div className={'page-content'}>
          <PageHeader
            title={vocabulary.labels.divisions}
            downloadReport={this.downloadDivisionsReport}
            btnText={vocabulary.buttons.reports.downloadReportPdf}
            btnTextDocx={vocabulary.buttons.reports.downloadReportDocx}
          />
          <Container>{this.renderEntityPanels()}</Container>
        </div>
        {this.state.locked && (
          <Spinner isFullSize message={vocabulary.preloader.waitForReport} />
        )}
      </React.Fragment>
    );
  }
}

export default connector(DivisionsPage);
